import { useCallback, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  NotificationApi,
  notificationApiRef,
} from '../../../../../apis/notificationApi';
import { toolsProvisioningApiRef } from '../../../../../apis/toolsProvisioningApi';
import type {
  ProjectMappingType,
  MappedProject,
} from '../interfaces/ui/addProjectModal';
import { toMappedProject } from '../dto/responseDto/mappedProject.response.dto';

export const useMappedProjects = () => {
  const notificationApi: NotificationApi = useApi(notificationApiRef);
  const toolsProvisioningApi = useApi(toolsProvisioningApiRef);

  const [mappedProjects, setMappedProjects] = useState<MappedProject[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMappedProjects = useCallback(async (
    apmNumber: string,
    projectMappingType: ProjectMappingType | '',
  ) => {
    if (!apmNumber || !projectMappingType) {
      setMappedProjects([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await toolsProvisioningApi.getProvisionedProjectDetails(
        apmNumber,
        projectMappingType.toLowerCase(),
      );

      if (!response || response?.data?.status?.toLowerCase() !== 'success') {
        throw new Error(`Failed to fetch mapped projects. Please try again.`);
      }

      const mappedProjects: MappedProject[] = toMappedProject(response);

      if (mappedProjects.length === 0) {
        setMappedProjects([]);

        setError(
          `No mapped projects found for the selected APM in ${projectMappingType}. Please map your project first and try again.`,
        );
      }
      setMappedProjects(mappedProjects);
    } catch (error) {
      setMappedProjects([]);
      setError(`Error occurred - ${error}`);

      notificationApi.sendNotification({
        message: `Error occurred - ${error}`,
        disapperAfterMs: 4000,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [toolsProvisioningApi, notificationApi]);

  const clearMappedProjectsFetchError = useCallback(() => setError(null), []);

  return {
    fetchMappedProjects,
    mappedProjects,
    loadingMappedProjects: loading,
    mappedProjectsError: error,
    clearMappedProjectsFetchError,
  };
};
